// components/affiliateGap/GapSpy.js - Updated to use gapBrandsSlice with /api/addGapBrand/
import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Eye, Loader2, AlertCircle, CheckCircle, Crown, Zap, ChevronDown } from 'lucide-react';
import GapCard from './gapCard';
import SingleCountrySelect from '../common/SingleCountrySelect';
import ReferralCsvUpload from '../common/ReferralCsvUpload';
import {
  canOverrideVerification,
  verifyBrandUrl,
  verifyTradingBrandUrl,
  fetchTradingAccess,
  overrideBrandUrl
} from '../../services/urlVerification';
import { useAdminSetting } from '../../services/adminSettings';
import { useAccount, canAddBrand } from '../../services/account';
import {
  fetchGapBrands,
  addGapBrand,
  updateFormData,
  resetFormData,
  clearError,
  clearAddError,
  clearSuccess,
  selectGapBrands,
  selectGapBrandsLoading,
  selectGapFormData,
  selectGapIsAdding,
  selectGapAddError,
  selectGapSuccessMessage,
  startGapPollingWithCleanup
} from '../../store/slices/gapBrandsSlice';
// The user's own dashboard brands feed the "Your Brand" dropdown — the gap is
// measured against a brand they've already added, not a free-typed URL.
import { fetchBrands, selectBrands, selectBrandsLoading } from '../../store/slices/brandsSlice';

// Loading Popup Component - Slimmer and Professional
const LoadingPopup = ({ isOpen, progress, timeRemaining }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 dark:bg-opacity-70 flex items-center justify-center z-50">
      <div className="bg-white dark:bg-slate-800 rounded-lg border border-gray-200 dark:border-slate-700 shadow-2xl p-6 max-w-sm w-full mx-4">
        <div className="text-center">
          {/* Header */}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-800 dark:text-slate-100">Processing Gap Analysis</h3>
          </div>

          {/* Loader */}
          <div className="mb-4">
            <div className="relative mx-auto w-12 h-12 mb-3">
              <Loader2 className="w-12 h-12 text-blue-600 dark:text-blue-500 animate-spin" />
            </div>
            <p className="text-gray-600 dark:text-slate-400 text-sm mb-1">Analyzing gap brand data...</p>
            <p className="text-xs text-gray-500 dark:text-slate-400">This process runs in the background</p>
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-2 overflow-hidden">
              <div
                className="bg-gradient-to-r from-blue-600 to-blue-700 dark:from-blue-500 dark:to-blue-600 h-2 rounded-full transition-all duration-1000 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <div className="flex justify-between mt-1 text-xs text-gray-500 dark:text-slate-400">
              <span>{Math.round(progress)}% complete</span>
              <span>{timeRemaining}s remaining</span>
            </div>
          </div>

          {/* Status Messages */}
          <div className="text-xs text-gray-600 dark:text-slate-400">
            {progress < 30 && "Initializing analysis..."}
            {progress >= 30 && progress < 60 && "Processing brand data..."}
            {progress >= 60 && progress < 90 && "Finalizing results..."}
            {progress >= 90 && progress < 100 && "Almost done..."}
            {progress === 100 && (
              <div className="flex items-center justify-center gap-2 text-green-600 dark:text-green-500">
                <CheckCircle className="w-4 h-4" />
                <span>Analysis started!</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Gap Limit Exceeded Component
const GapLimitExceeded = ({ limitData, onUpgrade, onClose }) => {
  const getPlanColor = (plan) => {
    const colors = {
      free: 'text-gray-600',
      growth: 'text-green-600',
      pro: 'text-blue-600',
      enterprise: 'text-purple-600'
    };
    return colors[plan] || 'text-gray-600';
  };

  return (
    <div className="mb-6 p-6 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-700 rounded-xl shadow-sm">
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-amber-100 dark:bg-amber-800 rounded-full flex items-center justify-center">
            <Crown className="w-6 h-6 text-amber-600 dark:text-amber-400" />
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-amber-900 dark:text-amber-100">
              Gap Analysis Limit Reached
            </h3>
          </div>

          <p className="text-amber-800 dark:text-amber-200 mb-4">
            You've analyzed <strong>{limitData?.currentGapCount || 0}</strong> out of <strong>{limitData?.allowedGaps || 0}</strong> gap brands
            allowed on your <span className={`font-semibold capitalize ${getPlanColor(limitData?.userPlan)}`}>
              {limitData?.userPlan || 'free'}
            </span> plan.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onUpgrade}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-medium rounded-lg transition-all duration-200 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
            >
              <Crown className="w-4 h-4" />
              <span>View Pricing Plans</span>
            </button>

            <button
              onClick={onClose}
              className="inline-flex items-center gap-2 px-6 py-3 bg-white hover:bg-gray-50 text-amber-800 font-medium rounded-lg border border-amber-300 transition-all duration-200"
            >
              <span>Maybe Later</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const GapSpy = () => {
  const dispatch = useDispatch();

  // Use gapBrandsSlice selectors
  const brands = useSelector(selectGapBrands);
  const isLoadingBrands = useSelector(selectGapBrandsLoading);
  const formData = useSelector(selectGapFormData);
  const isAdding = useSelector(selectGapIsAdding);
  const addError = useSelector(selectGapAddError);
  const successMessage = useSelector(selectGapSuccessMessage);

  // The user's own brands (dashboard) — options for the "Your Brand" select box.
  const dashboardBrands = useSelector(selectBrands);
  const isLoadingDashboardBrands = useSelector(selectBrandsLoading);

  const [formErrors, setFormErrors] = useState({});
  const [showLoadingPopup, setShowLoadingPopup] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [timeRemaining, setTimeRemaining] = useState(30);
  const [gapLimitData, setGapLimitData] = useState(null);
  const [showLimitExceeded, setShowLimitExceeded] = useState(false);
  const [urlVerified, setUrlVerified] = useState(false);
  const [verifyingUrl, setVerifyingUrl] = useState(false);
  const [verificationMessage, setVerificationMessage] = useState('');
  const [sameBrandWarning, setSameBrandWarning] = useState('');
  const [overriding, setOverriding] = useState(false);

  // Which product this gap brand belongs to. Accounts without the Trading &
  // Investment vertical never see the choice and stay on 'igaming' — the form
  // then behaves exactly as it did before this existed.
  const [verticalMode, setVerticalMode] = useState('igaming');
  const [tradingAccess, setTradingAccess] = useState({ allowed: false, subVerticals: [] });
  const isTrading = verticalMode === 'trading';
  // The instruments the broker check found on the site (CFDs, Forex, ETFs…).
  // This form has never had a vertical picker, so they are not asked for —
  // they ride along from the verification and land in brand.sub_vertical.
  const [detectedSubVerticals, setDetectedSubVerticals] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchTradingAccess().then((access) => {
      if (!cancelled) setTradingAccess(access);
    });
    return () => { cancelled = true; };
  }, []);

  // Switching product invalidates the verification: the URL was checked against
  // the wrong question ("is this a casino?" vs "is this a broker?").
  const handleVerticalModeChange = (mode) => {
    if (mode === verticalMode) return;
    setVerticalMode(mode);
    setUrlVerified(false);
    setVerificationMessage('');
    setDetectedSubVerticals([]);
  };

  // Admin only: state that a URL is a brand when the classifier disagrees, and
  // supply the competitor's referring domains as a CSV instead of using SEMrush.
  // The CSV picker is hidden until it is switched on in Settings → Admin
  // Settings, so the form stays clean on the runs that don't need it.
  const { user } = useSelector((state) => state.auth);
  const canOverrideUrl = canOverrideVerification(user);
  const csvUploadEnabled = useAdminSetting('csvUpload');
  // Team login: may this person add affiliate gaps? (owners always may)
  const { account: teamAccount } = useAccount();
  const canUploadCsv = canOverrideVerification(user) && csvUploadEnabled;
  const [csv, setCsv] = useState({ file: null, rows: [] });

  // Switching it off mid-form drops any file already picked, so a hidden CSV
  // can never ride along on the next submit.
  useEffect(() => {
    if (!canUploadCsv) setCsv({ file: null, rows: [] });
  }, [canUploadCsv]);

  /**
   * Accept the URL as a brand and remember it. The domain lands in the same
   * cache the ordinary check reads, so it is verified in Add Project and
   * Competitor Spy too, not just here.
   */
  const handleOverrideUrl = async () => {
    const trimmedUrl = formData.spyUrl?.trim() || '';
    if (!trimmedUrl) return;

    setOverriding(true);
    const result = await overrideBrandUrl(trimmedUrl);

    setUrlVerified(result.verified);
    setVerificationMessage(result.message);
    setOverriding(false);
  };

  // Refs to track timers independently of re-renders
  const popupTimerRef = useRef(null);
  const progressIntervalRef = useRef(null);
  const popupOpenTimeRef = useRef(null);
  const verifyTimerRef = useRef(null);

  // Fetch brands ONLY once on component mount — gap brands (the cards below)
  // plus the user's dashboard brands (options for the "Your Brand" dropdown).
  useEffect(() => {
    dispatch(fetchGapBrands());
    dispatch(fetchBrands());
  }, [dispatch]);

  // Clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        dispatch(clearSuccess());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage, dispatch]);

  // Clear add error after 7 seconds
  useEffect(() => {
    if (addError) {
      const timer = setTimeout(() => {
        dispatch(clearAddError());
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [addError, dispatch]);

  // Start popup timers when popup opens
  useEffect(() => {
    if (!showLoadingPopup) return;

    // Record when popup opened
    popupOpenTimeRef.current = Date.now();

    // Reset values
    setLoadingProgress(0);
    setTimeRemaining(30);

    // Progress bar animation - complete in 30 seconds
    const progressInterval = setInterval(() => {
      setLoadingProgress(prev => {
        if (prev >= 100) return 100;
        return prev + (100 / 30); // ~3.33% per second = 100% in 30 seconds
      });
      setTimeRemaining(prev => Math.max(0, prev - 1));
    }, 1000);

    // Force close popup after exactly 30 seconds
    const closeTimer = setTimeout(() => {
      setShowLoadingPopup(false);
      dispatch(fetchGapBrands());
    }, 30000);

    // Store in refs for cleanup
    progressIntervalRef.current = progressInterval;
    popupTimerRef.current = closeTimer;

    return () => {
      clearInterval(progressInterval);
      clearTimeout(closeTimer);
      progressIntervalRef.current = null;
      popupTimerRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLoadingPopup]);

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (!formData.brandName.trim()) {
      newErrors.brandName = 'Brand name is required';
      isValid = false;
    }

    if (!formData.spyUrl.trim()) {
      newErrors.spyUrl = 'Competitor website URL is required';
      isValid = false;
    } else {
      // Basic URL validation — allow multi-part domains (e.g. apostatudo.bet.br,
      // sportingbet.com.br, foo.co.uk). The old pattern allowed exactly two
      // labels, so every operator on a second-level ccTLD was refused before
      // anything was even asked of the server. Same pattern as Competitor Spy.
      const urlPattern = /^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
      const cleanUrl = formData.spyUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];

      if (!urlPattern.test(cleanUrl)) {
        newErrors.spyUrl = 'Please enter a valid domain (e.g., example.com)';
        isValid = false;
      }
    }

    // Your brand comes from a dropdown of the user's own dashboard brands, so a
    // presence check is enough — no free-text domain validation needed.
    if (!formData.userBrandUrl?.trim()) {
      newErrors.userBrandUrl = 'Please select your brand';
      isValid = false;
    }

    if (!formData.country) {
      newErrors.country = 'Please select the country where this competitor is promoting';
      isValid = false;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const handleInputChange = (field, value) => {
    // Block HTML/script injection
    if (/<[^>]*>/g.test(value)) return;

    // Brand name: only letters, numbers, spaces, dots, hyphens, ampersands
    if (field === 'brandName' && value && !/^[a-zA-Z0-9\s.\-&]*$/.test(value)) return;

    // URL fields: block spaces and invalid chars
    if ((field === 'spyUrl' || field === 'userBrandUrl') && value && /[\s<>"'{}|\\^`\[\]]/.test(value)) return;

    dispatch(updateFormData({ [field]: value }));

    // Reset URL verification when competitor URL changes
    if (field === 'spyUrl') {
      setUrlVerified(false);
      setVerificationMessage('');
    }

    // Clear field error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Clear same-brand warning when either URL field is edited
    if ((field === 'spyUrl' || field === 'userBrandUrl') && sameBrandWarning) {
      setSameBrandWarning('');
    }

    if (addError) dispatch(clearAddError());

    if (showLimitExceeded) {
      setShowLimitExceeded(false);
      setGapLimitData(null);
    }
  };

  // Auto-verify URL when user stops typing a valid domain
  useEffect(() => {
    const trimmedUrl = formData.spyUrl?.trim() || '';

    // Reset if empty
    if (!trimmedUrl) {
      setUrlVerified(false);
      setVerificationMessage('');
      return;
    }

    // Check if it looks like a valid domain. Any number of labels: an operator
    // on .bet.br, .com.br or .co.uk is as ordinary as one on .com, and the
    // two-label version of this silently returned here — no request, no error,
    // just a field that never verified.
    const domainPattern = /^([a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;
    const cleanUrl = trimmedUrl.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    if (!domainPattern.test(cleanUrl)) return;

    // Debounce: wait 800ms after user stops typing
    if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);

    verifyTimerRef.current = setTimeout(async () => {
      setVerifyingUrl(true);
      setVerificationMessage('');

      // A domain verified before — here, in Add Project or in Competitor Spy —
      // answers from the verification cache without the fetch or the AI call.
      // Same call, different question: the trading check asks "is this a
      // forex/CFD broker?" and returns the instruments it offers.
      const result = isTrading
        ? await verifyTradingBrandUrl(trimmedUrl)
        : await verifyBrandUrl(trimmedUrl);

      setUrlVerified(result.verified);
      setVerificationMessage(result.message);
      setDetectedSubVerticals(result.verified && isTrading ? (result.verticals || []) : []);
      setVerifyingUrl(false);
    }, 800);

    return () => {
      if (verifyTimerRef.current) clearTimeout(verifyTimerRef.current);
    };
  }, [formData.spyUrl, isTrading]);

  const handleStartAnalysis = async () => {
    if (!validateForm()) {
      return;
    }

    if (!urlVerified) {
      setFormErrors(prev => ({ ...prev, spyUrl: 'Please verify the URL before starting analysis' }));
      return;
    }

    // Clean the URLs
    const cleanUrl = formData.spyUrl.trim().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];
    const cleanUserBrandUrl = formData.userBrandUrl.trim().replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0];

    // Warn if the user picked the same brand for both fields (their own brand cannot be its own gap competitor)
    if (cleanUrl && cleanUserBrandUrl && cleanUrl.toLowerCase() === cleanUserBrandUrl.toLowerCase()) {
      setSameBrandWarning('Your brand and the gap (competitor) brand cannot be the same. Please choose a different competitor brand.');
      return;
    }
    setSameBrandWarning('');

    // Call the addGapBrand thunk
    try {
      const result = await dispatch(addGapBrand({
        brandName: formData.brandName.trim(),
        brandUrl: cleanUrl,
        userBrandUrl: cleanUserBrandUrl,
        selectedVerticals: ['Casino'],
        selectedCountries: [formData.country],
        // Which classifier judges this brand's referring domains. The server
        // re-checks that the account actually has the vertical (and replaces
        // selectedVerticals above with 'Trading & Investment' when it does), so
        // sending it is harmless for anyone else.
        verticalMode,
        ...(isTrading && detectedSubVerticals.length > 0
          ? { subVerticals: detectedSubVerticals }
          : {}),
        // Admin only: use the uploaded referring domains instead of SEMrush.
        ...(canUploadCsv && csv.rows.length > 0
          ? { csvBacklinks: csv.rows, skipSemrush: true }
          : {})
      }));

      // Check if the action was rejected due to gap limit
      if (result.type === 'gapBrands/addGapBrand/rejected') {
        const errorPayload = result.payload;

        // Check if it's a gap limit error
        if (errorPayload?.error === 'GAP_LIMIT_EXCEEDED' && errorPayload?.data) {
          setGapLimitData(errorPayload.data);
          setShowLimitExceeded(true);
          return; // Don't show loading popup
        }
      }

      // If successful, show loading popup and start polling
      if (result.type === 'gapBrands/addGapBrand/fulfilled') {
        setShowLoadingPopup(true);
        // Drop the CSV so the next brand added on this page doesn't silently
        // reuse the previous competitor's referring domains.
        setCsv({ file: null, rows: [] });

        // Start polling for the new brand. initialStatus matters for instant
        // copies (brand existed for another user): the pipeline is already
        // done ('8') and only the background live-affiliate check is running,
        // so polling starts on the "Verifying live affiliates" step instead of
        // flashing "step 1".
        if (result.payload.brand) {
          dispatch(startGapPollingWithCleanup({
            brandUrl: result.payload.brand.brand_url,
            brandName: result.payload.brand.brand_name,
            brandId: result.payload.brand.id,
            initialStatus: result.payload.brand.processing_status
          }));
        }
      }

    } catch (error) {
      console.error('Gap analysis error:', error);
    }
  };

  const handleUpgrade = () => {
    // Redirect to pricing/upgrade page
    window.open('/pricing-plan', '_blank');
  };

  const handleCloseLimitMessage = () => {
    setShowLimitExceeded(false);
    setGapLimitData(null);
  };

  // Handle brand status updates from GapCard
  const handleBrandStatusUpdate = (brandId, status) => {
    // GapCard only reports 'completed' once the pipeline AND the background
    // live-affiliate check are both done, so the data is final — refresh now.
    if (status === 'completed') {
      dispatch(fetchGapBrands());
    }
  };

  return (
    <div className="p-4 min-h-screen bg-gray-100 dark:bg-slate-950">
      {/* Loading Popup */}
      <LoadingPopup
        isOpen={showLoadingPopup}
        progress={loadingProgress}
        timeRemaining={timeRemaining}
      />

      <div className="mb-6">
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <h1 className="text-2xl font-semibold mb-2 text-gray-800 dark:text-slate-100 flex items-center gap-3">
            <Eye className="w-6 h-6 text-blue-600 dark:text-blue-500" />
            Affiliate Gap Analysis
          </h1>
          <p className="text-gray-600 dark:text-slate-400">
            Find affiliates working with competitors but not with your brand
          </p>
        </div>
      </div>

      {/* Gap Limit Exceeded Message */}
      {showLimitExceeded && gapLimitData && (
        <GapLimitExceeded
          limitData={gapLimitData}
          onUpgrade={handleUpgrade}
          onClose={handleCloseLimitMessage}
        />
      )}

      {/* A team member may only view the affiliate gaps shared with them. */}
      {!canAddBrand(teamAccount, 'gaps') && (
        <div className="mb-6 rounded-xl border border-blue-200 dark:border-blue-900 bg-blue-50 dark:bg-blue-950/30 px-4 py-3 text-sm text-blue-800 dark:text-blue-300">
          You can view the affiliate gaps your account admin shared with you. Adding new ones is turned off for your login.
        </div>
      )}
      <div className={`mb-6 ${canAddBrand(teamAccount, 'gaps') ? '' : 'hidden'}`}>
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-800 dark:text-slate-100">Add Gap Brand</h2>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="mb-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 dark:bg-green-400 rounded-full"></div>
                <p className="text-green-700 dark:text-green-300 font-medium">{successMessage}</p>
              </div>
              <p className="text-green-600 dark:text-green-400 text-sm mt-1">
                The brand will appear below and will be processed automatically.
              </p>
            </div>
          )}

          {/* General Error Message */}
          {addError && !showLimitExceeded && (
            <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400" />
                <p className="text-red-700 dark:text-red-300 font-medium">{addError}</p>
              </div>
            </div>
          )}

          {/* Same-Brand Warning - Yellow */}
          {sameBrandWarning && !showLimitExceeded && (
            <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                <p className="text-yellow-800 dark:text-yellow-200 font-medium">{sameBrandWarning}</p>
              </div>
            </div>
          )}

          {/* Only accounts granted the Trading & Investment vertical get this
              choice. Everyone else sees the form unchanged. */}
          {tradingAccess.allowed && (
            <div className="mb-6 max-w-xl">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                What kind of brand is this?
              </label>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { id: 'igaming', title: 'iGaming', hint: 'Casino, betting, poker…' },
                  { id: 'trading', title: 'Trading & Investment', hint: 'Forex / CFD brokers' }
                ].map(option => (
                  <button
                    key={option.id}
                    type="button"
                    onClick={() => handleVerticalModeChange(option.id)}
                    disabled={isAdding || showLoadingPopup}
                    className={`text-left px-4 py-3 rounded-lg border transition-all disabled:cursor-not-allowed disabled:opacity-60 ${
                      verticalMode === option.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 dark:border-blue-500 ring-2 ring-blue-500/30'
                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <span className="block text-sm font-semibold text-gray-800 dark:text-slate-100">{option.title}</span>
                    <span className="block text-xs text-gray-500 dark:text-slate-400 mt-0.5">{option.hint}</span>
                  </button>
                ))}
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-slate-400">
                This decides how the URL is verified and how the affiliates found for this brand are classified.
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Brand Name *
              </label>
              <input
                type="text"
                value={formData.brandName}
                onChange={(e) => handleInputChange('brandName', e.target.value)}
                placeholder="e.g., BetWin Casino"
                disabled={isAdding || showLoadingPopup}
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors dark:bg-slate-800 dark:text-slate-100 ${
                  formErrors.brandName ? 'border-red-300 dark:border-red-700 focus:ring-red-500' : 'border-gray-200 dark:border-slate-700'
                } ${(isAdding || showLoadingPopup) ? 'bg-gray-50 dark:bg-slate-900 cursor-not-allowed' : ''}`}
              />
              {formErrors.brandName && (
                <p className="mt-1 text-red-600 dark:text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.brandName}
                </p>
              )}
            </div>

            {/* Not pb-6 with absolutely-positioned messages any more: a
                rejection runs to three lines and printed itself over the
                label. */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Competitor Website URL *
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={formData.spyUrl}
                  onChange={(e) => handleInputChange('spyUrl', e.target.value)}
                  placeholder={isTrading ? 'e.g., icmarkets.com' : 'e.g., betwin-casino.com'}
                  disabled={isAdding || showLoadingPopup}
                  className={`w-full px-4 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors dark:bg-slate-800 dark:text-slate-100 ${
                    formErrors.spyUrl ? 'border-red-300 dark:border-red-700 focus:ring-red-500' : urlVerified ? 'border-green-400 dark:border-green-600' : 'border-gray-200 dark:border-slate-700'
                  } ${(isAdding || showLoadingPopup) ? 'bg-gray-50 dark:bg-slate-900 cursor-not-allowed' : ''}`}
                />
                {verifyingUrl && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                  </div>
                )}
                {!verifyingUrl && urlVerified && (
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                  </div>
                )}
              </div>
              {formErrors.spyUrl && (
                <p className="mt-1 text-red-600 dark:text-red-400 text-sm flex items-start gap-1">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {formErrors.spyUrl}
                </p>
              )}
              {verificationMessage && !formErrors.spyUrl && (
                <p className={`mt-1 text-sm flex items-start gap-1 ${urlVerified ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {urlVerified
                    ? <CheckCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                    : <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />}
                  <span>{verificationMessage}</span>
                  {/* Admin only, and only once the check has come back with a
                      no — the classifier rejects sites it should not. Saying
                      so once stores the domain as verified for every form.
                      iGaming only: the override writes a GAMBLING verdict, and
                      the broker check reads its own cache. */}
                  {canOverrideUrl && !isTrading && !urlVerified && !verifyingUrl && (
                    <button
                      type="button"
                      onClick={handleOverrideUrl}
                      disabled={overriding}
                      className="ml-1 font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:cursor-wait disabled:opacity-60"
                    >
                      {overriding ? 'Saving override…' : 'Override'}
                    </button>
                  )}
                </p>
              )}
            </div>

            <div className="relative pb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Your Brand *
              </label>
              <div className="relative">
                <select
                  value={formData.userBrandUrl}
                  onChange={(e) => handleInputChange('userBrandUrl', e.target.value)}
                  disabled={isAdding || showLoadingPopup || isLoadingDashboardBrands || dashboardBrands.length === 0}
                  className={`w-full appearance-none px-4 py-3 pr-10 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 transition-colors bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100 ${
                    formErrors.userBrandUrl ? 'border-red-300 dark:border-red-700 focus:ring-red-500' : 'border-gray-200 dark:border-slate-700'
                  } ${(isAdding || showLoadingPopup || isLoadingDashboardBrands || dashboardBrands.length === 0) ? 'bg-gray-50 dark:bg-slate-900 cursor-not-allowed' : ''}`}
                >
                  <option value="">
                    {isLoadingDashboardBrands
                      ? 'Loading your brands...'
                      : dashboardBrands.length === 0
                        ? 'No brands in your dashboard yet'
                        : 'Select your brand'}
                  </option>
                  {dashboardBrands.map((b) => (
                    <option key={b.id || b.brand_url} value={b.brand_url}>
                      {b.brand_name ? `${b.brand_name} (${b.brand_url})` : b.brand_url}
                    </option>
                  ))}
                </select>
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                  <ChevronDown className="w-4 h-4 text-gray-400 dark:text-slate-500" />
                </div>
              </div>
              {formErrors.userBrandUrl ? (
                <p className="absolute left-0 bottom-0 text-red-600 dark:text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.userBrandUrl}
                </p>
              ) : (
                <p className="absolute left-0 bottom-0 text-xs text-gray-500 dark:text-slate-400">
                  {dashboardBrands.length === 0 && !isLoadingDashboardBrands
                    ? 'Add a brand in your dashboard first to compare against.'
                    : 'Pick one of your dashboard brands to compare against the competitor'}
                </p>
              )}
            </div>

            <div className="relative pb-6">
              <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                Country *
              </label>
              <SingleCountrySelect
                value={formData.country}
                onChange={(code) => handleInputChange('country', code)}
                disabled={isAdding || showLoadingPopup}
                error={formErrors.country}
              />
              {formErrors.country ? (
                <p className="absolute left-0 bottom-0 text-red-600 dark:text-red-400 text-sm flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  {formErrors.country}
                </p>
              ) : (
                <p className="absolute left-0 bottom-0 text-xs text-gray-500 dark:text-slate-400">
                  Market where the competitor is promoting — drives its affiliate stats
                </p>
              )}
            </div>
          </div>

          {/* Admin only: skip SEMrush and use an uploaded referring-domain list
              for the competitor brand. Falls back to SEMrush when left empty. */}
          {canUploadCsv && (
            <div className="mt-2 max-w-xl">
              <ReferralCsvUpload
                file={csv.file}
                rows={csv.rows}
                onChange={setCsv}
                disabled={isAdding || showLoadingPopup}
              />
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={handleStartAnalysis}
              disabled={isAdding || showLoadingPopup}
              className={`font-medium py-3 px-6 rounded-lg transition-all duration-300 flex items-center gap-2 ${
                (isAdding || showLoadingPopup)
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {(isAdding || showLoadingPopup) ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Adding Brand...</span>
                </>
              ) : (
                <>
                  <Search className="w-4 h-4" />
                  <span>{csv.rows.length > 0 ? 'Add Gap Brand from CSV' : 'Add Gap Brand'}</span>
                </>
              )}
            </button>
            {(isAdding || showLoadingPopup) && (
              <p className="text-sm text-gray-600 dark:text-slate-400 mt-2">
                This may take a few moments. The brand will appear below once processing begins.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Brand Cards Section */}
      <div className="space-y-6">
        {/* Spinner only on the FIRST load. A background refresh must leave the
            cards mounted: swapping them for a spinner unmounts every GapCard,
            and a remounted card re-fires its one-shot completion callback, which
            dispatches another refresh — a loop that never settles. */}
        {isLoadingBrands && brands.length === 0 ? (
          <div className="flex justify-center items-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-600 dark:text-blue-500" />
            <span className="ml-2 text-gray-600 dark:text-slate-400">Loading gap brands...</span>
          </div>
        ) : brands.length > 0 ? (
          <>
            <div className="mb-4">
              <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100">
                Your Gap Brands ({brands.length})
              </h3>
            </div>
            {brands.map((brand) => (
              <GapCard
                key={brand.id || brand.brand_url}
                brand={brand}
                onStatusUpdate={handleBrandStatusUpdate}
              />
            ))}
          </>
        ) : (
          <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 shadow-sm p-8 text-center">
            <Eye className="w-12 h-12 text-gray-400 dark:text-slate-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-800 dark:text-slate-100 mb-2">No Gap Brands Yet</h3>
            <p className="text-gray-600 dark:text-slate-400">
              Start by adding your first gap brand to analyze.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default GapSpy;
